import {
  Box,
  Divider,
  Stack,
  Typography,
  IconButton,
  Popper,
  Paper,
  ClickAwayListener,
  FilledInput,
  Button,
  MenuItem,
} from "@mui/material";
import { FC, useState, useRef } from "react";
import { TfiHeadphoneAlt } from "react-icons/tfi";
import SearchIcon from "@mui/icons-material/Search";
import { HeaderNavInterface } from "src/lib/utils/interfaces";
import { effectiveNovaPressSubmenu } from "../effective-submenu";

export interface NovaPressDesktopNavLinksProps {
  nav: Array<HeaderNavInterface>;
  isActive: (item: HeaderNavInterface, sectionId: string | undefined) => boolean;
  onClick: (item: HeaderNavInterface, sectionId: string | undefined) => void;
  showSearch?: boolean;
  searchPlaceholder?: string;
}

export const NovaPressDesktopNavLinks: FC<NovaPressDesktopNavLinksProps> = ({
  nav,
  isActive,
  onClick,
  showSearch = false,
  searchPlaceholder = "Tìm kiếm sản phẩm...",
}) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const searchAnchorRef = useRef<HTMLButtonElement>(null);

  const handleSearchToggle = () => setSearchOpen((prev) => !prev);
  const handleSearchClose = () => setSearchOpen(false);
  const handleSearchSubmit = (searchValue: string) => {
    console.log("Search:", searchValue);
    setSearchOpen(false);
  };

  return (
    <Box display="flex" gap={3} alignItems="stretch" height="100%" px={1} justifyContent="center">
      {nav.map((item, index) => {
        const sectionId = item.target?.replace("#", "");
        const isItemActive = isActive(item, sectionId);
        const submenuItems = effectiveNovaPressSubmenu(item);
        const hasDropdown = submenuItems.length > 0;
        const useAnchorShell = item.action === "link" && item.target && !hasDropdown;

        const labelNode =
          item.action === "link" && item.target && hasDropdown ? (
            <Typography
              component="a"
              href={item.target}
              onClick={(e) => e.stopPropagation()}
              sx={{
                lineHeight: 1.5,
                fontWeight: isItemActive ? 800 : 700,
                color: "inherit",
                textDecoration: "none",
              }}
            >
              {item.label}
            </Typography>
          ) : (
            <Typography sx={{ lineHeight: 1.5, fontWeight: isItemActive ? 800 : 700 }}>
              {item.label}
            </Typography>
          );

        const scrollRowClick =
          item.action === "scroll"
            ? (e: React.MouseEvent) => {
                const el = e.target as HTMLElement;
                if (el.closest(".np-dd")) return;
                e.preventDefault();
                onClick(item, sectionId);
              }
            : undefined;

        return (
          <Box
            key={index}
            component={useAnchorShell ? "a" : "div"}
            href={useAnchorShell ? item.target : undefined}
            onClick={scrollRowClick}
            display="flex"
            alignItems="center"
            justifyContent="center"
            alignSelf="stretch"
            sx={{
              px: 1.5,
              py: 0,
              textDecoration: "none",
              color: "grey.900",
              borderBottom: "3px solid",
              borderColor: isItemActive ? "primary.main" : "transparent",
              "&:hover": {
                cursor: "pointer",
                color: "grey.900",
                borderColor: "primary.main",
              },
            }}
          >
            {hasDropdown ? (
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  maxWidth: "100%",
                  "&:hover .np-dd": {
                    opacity: 1,
                    visibility: "visible",
                    pointerEvents: "auto",
                  },
                }}
              >
                {labelNode}
                <Box
                  className="np-dd"
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: "100%",
                    pt: 1.25,
                    minWidth: 220,
                    zIndex: 2000,
                    opacity: 0,
                    visibility: "hidden",
                    pointerEvents: "none",
                    transition: "opacity 0.12s ease, visibility 0.12s ease",
                  }}
                >
                  <Paper elevation={3} sx={{ py: 0.5 }}>
                    {submenuItems.map((subItem, subIndex) => {
                      const subSectionId = subItem.target?.replace("#", "");
                      const isSubItemActive = isActive(subItem, subSectionId);

                      return (
                        <MenuItem
                          key={subIndex}
                          component={subItem.action === "link" ? "a" : "div"}
                          href={subItem.action === "link" ? subItem.target : undefined}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (subItem.action === "scroll") {
                              e.preventDefault();
                              onClick(subItem, subSectionId);
                            }
                          }}
                          sx={{
                            px: 2,
                            py: 1.25,
                            fontWeight: isSubItemActive ? 800 : 700,
                            color: isSubItemActive ? "primary.main" : "inherit",
                            "&:hover": {
                              backgroundColor: "primary.light",
                              color: "primary.main",
                            },
                          }}
                        >
                          {subItem.label}
                        </MenuItem>
                      );
                    })}
                  </Paper>
                </Box>
              </Box>
            ) : (
              labelNode
            )}
          </Box>
        );
      })}
      {showSearch && (
        <>
          <IconButton
            ref={searchAnchorRef}
            onClick={handleSearchToggle}
            size="small"
            sx={{
              color: "text.grey",
              p: 1,
              ml: 0.5,
              alignSelf: "center",
            }}
          >
            <SearchIcon />
          </IconButton>

          <Popper
            open={searchOpen}
            anchorEl={searchAnchorRef.current}
            placement="bottom-end"
            style={{ zIndex: 1300 }}
          >
            <ClickAwayListener onClickAway={handleSearchClose}>
              <Paper
                elevation={3}
                sx={{
                  p: 2.5,
                  mt: 1.5,
                  minWidth: 300,
                  maxWidth: 400,
                }}
              >
                <SearchDropdown
                  placeholder={searchPlaceholder}
                  onSubmit={handleSearchSubmit}
                  onClose={handleSearchClose}
                />
              </Paper>
            </ClickAwayListener>
          </Popper>
        </>
      )}
    </Box>
  );
};

NovaPressDesktopNavLinks.displayName = "NovaPressDesktopNavLinks";

export const NovaPressDesktopHotlineBlock: FC<{ hotline: string | undefined }> = ({ hotline }) => {
  if (!hotline) return null;
  return (
    <Stack direction="row" alignItems="center" alignSelf="stretch" spacing={2} px={0}>
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        sx={{
          cursor: "pointer",
          px: 1.5,
          py: 1,
        }}
        onClick={() => {
          window.location.href = `tel:${hotline}`;
        }}
      >
        <TfiHeadphoneAlt size={32} />
        <Stack spacing={0.25}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: "text.grey", lineHeight: 1.2 }}
          >
            Hotline
          </Typography>
          <Typography sx={{ color: "text.black", lineHeight: 1.2 }} fontWeight={800}>
            {hotline}
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
};

NovaPressDesktopHotlineBlock.displayName = "NovaPressDesktopHotlineBlock";

export interface NovaPressDesktopHeaderNavProps extends NovaPressDesktopNavLinksProps {
  hotline: string | undefined;
}

/** Inline layout: nav + divider + hotline (legacy). Prefer grid in `nova-press.tsx` for centered nav. */
export const NovaPressDesktopHeaderNav: FC<NovaPressDesktopHeaderNavProps> = ({
  hotline,
  ...linkProps
}) => (
  <Stack
    direction="row"
    alignItems="stretch"
    sx={{
      height: "100%",
      minHeight: { xs: 56, laptop: 64 },
    }}
    divider={
      <Divider
        orientation="vertical"
        variant="middle"
        flexItem
        sx={{ mx: 2.5, borderColor: "grey.400", borderWidth: 1 }}
      />
    }
  >
    <NovaPressDesktopNavLinks {...linkProps} />
    <NovaPressDesktopHotlineBlock hotline={hotline} />
  </Stack>
);

NovaPressDesktopHeaderNav.displayName = "NovaPressDesktopHeaderNav";

const SearchDropdown: FC<{
  placeholder: string;
  onSubmit: (value: string) => void;
  onClose: () => void;
}> = ({ placeholder, onSubmit, onClose }) => {
  const [searchValue, setSearchValue] = useState("");

  const handleSubmit = () => {
    if (searchValue.trim()) {
      onSubmit(searchValue.trim());
      setSearchValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <Stack spacing={2.5}>
      <FilledInput
        fullWidth
        autoFocus
        hiddenLabel
        placeholder={placeholder}
        disableUnderline
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyDown={handleKeyDown}
        sx={{
          borderRadius: "8px",
          backgroundColor: "grey.100",
          px: 1.5,
          py: 1.25,
        }}
      />
      <Stack direction="row" spacing={1.5} justifyContent="flex-end">
        <Button variant="outlined" size="small" onClick={onClose}>
          Hủy
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={!searchValue.trim()}
          sx={{
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            "&:hover": {
              backgroundColor: "primary.light",
            },
          }}
        >
          Tìm
        </Button>
      </Stack>
    </Stack>
  );
};
